import {
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";

import { useHeaderCollapse } from "@/components/layout/private/main/components/Header/HeaderCollapseContext";
import { getMobileContentTopInset } from "@/components/layout/private/main/components/Header/headerLayoutUtils";

interface MobileProps {
  title: string;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Segunda etapa de fluxo: seta à esquerda do título (ex.: voltar aos dados fitness). */
  onBack?: () => void;
  subtitle?: string;
  /** Soma ao z-index do modal — necessário acima de `MobileBottomSheet` (ex.: questionários). */
  zIndexOffset?: number;
}

/**
 * Tela mobile em fullscreen plana — colada abaixo do Header do app, com X para fechar.
 * Sem card/balão (borderRadius/sombra). Formulários curtos usam `MobileBottomSheet`.
 */
export function ScreenMobile({
  title,
  open,
  onClose,
  children,
  onBack,
  subtitle,
  zIndexOffset = 0,
}: MobileProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  // Sempre tenta o contexto do header; fora do layout, cai no inset padrão.
  const headerCollapse = useHeaderCollapse();
  const topInset =
    headerCollapse?.mobileHeaderSpacerHeight ??
    getMobileContentTopInset(theme);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      disableScrollLock
      // Abaixo do AppBar do Fluxmed — header global continua visível
      sx={{
        zIndex: theme.zIndex.drawer - 1 + zIndexOffset,
        // Dialog começa abaixo do header (colado, sem gap/balão)
        top: isMobileLayout ? topInset : 0,
        "& .MuiBackdrop-root": {
          top: isMobileLayout ? topInset : 0,
        },
      }}
      PaperProps={{
        sx: {
          m: 0,
          width: "100%",
          maxWidth: "100%",
          height: isMobileLayout
            ? `calc(100dvh - ${topInset})`
            : "100dvh",
          maxHeight: isMobileLayout
            ? `calc(100dvh - ${topInset})`
            : "100dvh",
          borderRadius: 0,
          boxShadow: "none",
          bgcolor: "background.default",
        },
      }}
    >
      <Stack
        direction="column"
        width="100%"
        height="100%"
        sx={{ minHeight: 0, bgcolor: "background.default" }}
      >
        {/* Barra plana colada no topo do Dialog (sem radius / sombra de card) */}
        <Stack
          width="100%"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.5,
            py: 1,
            minHeight: 52,
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {onBack ? (
              <IconButton
                size="small"
                aria-label="Voltar"
                onClick={onBack}
                sx={{ color: "neutral1000.main", flexShrink: 0 }}
              >
                <ArrowBack />
              </IconButton>
            ) : null}
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                color="neutral700.main"
                fontWeight={600}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {subtitle}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
          <IconButton
            sx={{ color: "neutral1000.main", flexShrink: 0 }}
            onClick={onClose}
            aria-label="Fechar"
          >
            <Close />
          </IconButton>
        </Stack>

        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {children}
        </Stack>
      </Stack>
    </Dialog>
  );
}
